import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { Mail, CheckCircle } from 'lucide-react'
import { supabase } from '@/lib/supabase'
import { Button, Input, Card } from '@/components/ui'
import { captureError } from '@/lib/sentry'

const schema = z.object({
  email: z.string().email('Enter a valid email address'),
})
type FormData = z.infer<typeof schema>

export default function AuthPage() {
  const [sent, setSent] = useState(false)
  const [sending, setSending] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const { register, handleSubmit, formState: { errors }, getValues } = useForm<FormData>({
    resolver: zodResolver(schema),
  })

  const onSubmit = async ({ email }: FormData) => {
    setSending(true)
    setError(null)
    try {
      const { error: authError } = await supabase.auth.signInWithOtp({
        email,
        options: { emailRedirectTo: window.location.origin },
      })
      if (authError) throw authError
      setSent(true)
    } catch (err) {
      captureError(err)
      setError('Could not send login link. Please try again.')
    } finally {
      setSending(false)
    }
  }

  return (
    <div className="min-h-screen bg-room-bg flex flex-col items-center justify-center px-4 py-12">
      {/* Ambient */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[500px] h-[500px] bg-room-accent/6 rounded-full blur-[120px]" />
      </div>

      <div className="relative z-10 w-full max-w-sm space-y-8">
        {/* Logo */}
        <div className="text-center space-y-2">
          <h1 className="text-5xl font-black">
            <span className="bg-gradient-to-br from-purple-300 via-room-accent to-purple-500 bg-clip-text text-transparent">
              The Room
            </span>
          </h1>
          <p className="text-room-muted text-sm">If you're not in The Room, you're missing it.</p>
        </div>

        <Card>
          {!sent ? (
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              <div className="space-y-1">
                <h2 className="text-white font-bold text-lg">Sign in</h2>
                <p className="text-room-muted text-sm">We'll send a magic link to your email — no password needed.</p>
              </div>

              <Input
                label="Email address"
                type="email"
                placeholder="you@example.com"
                autoComplete="email"
                autoFocus
                error={errors.email?.message}
                {...register('email')}
              />

              {error && <p className="text-room-error text-sm">{error}</p>}

              <Button type="submit" size="lg" loading={sending} icon={<Mail size={16} />} className="w-full">
                Send magic link
              </Button>
            </form>
          ) : (
            <div className="text-center space-y-4 py-4">
              <CheckCircle className="mx-auto text-room-success" size={40} />
              <div className="space-y-1">
                <p className="text-white font-bold">Check your inbox</p>
                <p className="text-room-muted text-sm">
                  Magic link sent to <span className="text-room-text">{getValues('email')}</span>
                </p>
              </div>
              <button
                onClick={() => setSent(false)}
                className="text-xs text-room-muted hover:text-room-text underline"
              >
                Use a different email
              </button>
            </div>
          )}
        </Card>

        <p className="text-center text-xs text-room-muted">
          Ibiza Pilot 2026 · Powered by The Room Network
        </p>
      </div>
    </div>
  )
}
